import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { useShoppingContext } from "../../../context/ShoppingContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faEdit } from "@fortawesome/free-solid-svg-icons";
import "./EditAlimento.css"; // Importar el archivo CSS compartido
import "../Edit.css"; // Importar el archivo CSS compartido

const EditAlimento = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { fetchAlimentos } = useShoppingContext();

  const [alimentoData, setAlimentoData] = useState({
    nombre: "",
    cantidad: 0,
    numeroSacos: 0,
    pesoSaco: 0,
  });

  // Estados para controlar si los campos están habilitados para la edición
  const [editableFields, setEditableFields] = useState({
    nombre: false,
    cantidad: false,
    numeroSacos: false,
    pesoSaco: false,
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_API_URL}/alimentos/${id}`)
      .then((response) => {
        const alimento = response.data;
        // Calcular número de sacos solo si pesoSaco es mayor que 0
        const numeroSacosCalculado =
          alimento.pesoSaco > 0 ? alimento.cantidad / alimento.pesoSaco : 0;

        setAlimentoData({
          ...alimento,
          numeroSacos: numeroSacosCalculado, // Asigna el valor calculado
        });
        setLoading(false);
      })
      .catch((error) => {
        setError("Error fetching alimento data.");
        setLoading(false);
      });
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Solo convertir a número si el campo no es "nombre"
    const newValue = name === "nombre" ? value : parseFloat(value) || 0; // Convertir a número flotante o usar 0 si no es un número

    setAlimentoData((prevData) => {
      let updatedData = { ...prevData, [name]: newValue };

      // Si estamos editando la cantidad, asegurarnos de que no sea NaN y recalcular el número de sacos
      if (name === "cantidad" && prevData.pesoSaco > 0) {
        updatedData.numeroSacos =
          newValue > 0 ? (newValue / prevData.pesoSaco).toFixed(2) : 0;
      }

      // Si estamos editando el número de sacos, recalcular la cantidad
      if (name === "numeroSacos" && prevData.pesoSaco > 0) {
        updatedData.cantidad =
          newValue > 0 ? (newValue * prevData.pesoSaco).toFixed(2) : 0;
      }

      // Si cambia el peso del saco, recalculamos tanto la cantidad como el número de sacos
      if (name === "pesoSaco" && newValue > 0) {
        updatedData.cantidad =
          prevData.numeroSacos > 0
            ? (prevData.numeroSacos * newValue).toFixed(2)
            : 0;
        updatedData.numeroSacos =
          prevData.cantidad > 0 ? (prevData.cantidad / newValue).toFixed(2) : 0;
      }

      return updatedData;
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    axios
      .put(`${process.env.REACT_APP_API_URL}/alimentos/${id}`, alimentoData)
      .then(() => {
        fetchAlimentos(); // Refresh the alimentos list after updating
        navigate("/configuracion"); // Navigate back to the list of alimentos
      })
      .catch((error) => {
        setError("Error updating alimento.");
      });
  };

  // Función para habilitar la edición de un campo
  const toggleEditField = (field) => {
    setEditableFields((prevFields) => ({
      ...prevFields,
      [field]: !prevFields[field], // Alterna entre habilitado y deshabilitado
    }));
  };

  if (loading) return <p>Loading...</p>;
  if (error) return <p>{error}</p>;

  return (
    <div className="edit-container">
      <button
        onClick={() => navigate(-1)}
        className="btn btn-back"
      >
        <FontAwesomeIcon icon={faArrowLeft} /> Volver
      </button>
      <h1 className="flex-space-between">
        {alimentoData?.nombre} <span className="IdEditContainer"> id: {alimentoData?.id}</span>{" "}
      </h1>{" "}
      <form onSubmit={handleSubmit}>
        {/* Nombre del alimento */}
        <div className="form-group">
          <label htmlFor="nombre">Nombre</label>
          <input
            type="text"
            id="nombre"
            name="nombre"
            value={alimentoData.nombre}
            onChange={handleChange}
            className="form-control"
            disabled={!editableFields.nombre} // Solo editable si se presionó "Modificar"
          />
          <button
            type="button"
            onClick={() => toggleEditField("nombre")}
            className="btn btn-edit"
          >
            <FontAwesomeIcon icon={faEdit} />{" "}
            {editableFields.nombre ? "Listo" : "Modificar"}
          </button>
        </div>

        {/* Cantidad total */}
        <div className="form-group">
          <label htmlFor="cantidad">Cantidad total (kg)</label>
          <input
            type="number"
            id="cantidad"
            name="cantidad"
            value={alimentoData.cantidad}
            onChange={handleChange}
            className="form-control"
            disabled={!editableFields.cantidad}
          />
          <button
            type="button"
            onClick={() => toggleEditField("cantidad")}
            className="btn btn-edit"
          >
            <FontAwesomeIcon icon={faEdit} />{" "}
            {editableFields.cantidad ? "Listo" : "Modificar"}
          </button>
        </div>

        {/* Peso por saco */}
        <div className="form-group">
          <label htmlFor="pesoSaco">Peso por saco (kg)</label>
          <input
            type="number"
            id="pesoSaco"
            name="pesoSaco"
            value={alimentoData.pesoSaco}
            onChange={handleChange}
            className="form-control"
            disabled={!editableFields.pesoSaco}
          />
          <button
            type="button"
            onClick={() => toggleEditField("pesoSaco")}
            className="btn btn-edit"
          >
            <FontAwesomeIcon icon={faEdit} />{" "}
            {editableFields.pesoSaco ? "Listo" : "Modificar"}
          </button>
        </div>

        {/* Número de sacos */}
        <div className="form-group">
          <label htmlFor="numeroSacos">Número de sacos</label>
          <input
            type="number"
            id="numeroSacos"
            name="numeroSacos"
            value={alimentoData.numeroSacos}
            onChange={handleChange}
            className="form-control"
            disabled={!editableFields.numeroSacos}
          />
          <button
            type="button"
            onClick={() => toggleEditField("numeroSacos")}
            className="btn btn-edit"
          >
            <FontAwesomeIcon icon={faEdit} />{" "}
            {editableFields.numeroSacos ? "Listo" : "Modificar"}
          </button>
        </div>

        <button type="submit" className="btn btn-primary">
          Guardar Cambios
        </button>
      </form>
    </div>
  );
};

export default EditAlimento;
